import {
  ACTION_CATEGORIES,
  TEAMMATES,
  type ActionCategory,
  type ConnectionEvent,
  type Decision,
} from "@typedoc/contracts/connections";

/**
 * The Activity log (Paper, "Component · Activity log", Oct 17): a connection's history read as a
 * short story by day. Everything here is pure, so the folding and the sentences are tested
 * without a browser; `activity-log.tsx` only draws what this returns.
 */

export type Who =
  | { kind: "teammate"; key: string; name: string }
  | { kind: "you" }
  | { kind: "someone" }
  | { kind: "abacor" };

export type WhoKey = string;

export const whoKey = (who: Who): WhoKey =>
  who.kind === "teammate" ? `teammate:${who.key}` : who.kind;

export const whoName = (who: Who) =>
  who.kind === "teammate"
    ? who.name
    : who.kind === "you"
      ? "You"
      : who.kind === "abacor"
        ? "Abacor"
        : "Someone";

/** People are stored by account ID; the reader is "You" and anyone else, for now, "Someone". */
export function whoOf(event: Pick<ConnectionEvent, "actorKind" | "actor">, viewerId: string): Who {
  if (event.actorKind === "teammate") {
    const key = event.actor ?? "";
    const name = TEAMMATES.find((teammate) => teammate.key === key)?.name ?? (key || "A teammate");
    return { kind: "teammate", key, name };
  }
  if (event.actorKind === "system") return { kind: "abacor" };
  return event.actor === viewerId ? { kind: "you" } : { kind: "someone" };
}

/** Work is what a teammate or a chat did, or a person's answer to it; the rest changed the account. */
const WORK_KINDS = new Set<ConnectionEvent["kind"]>([
  "tool_call",
  "tool_denied",
  "approval_requested",
  "approval_decided",
  "standing_approval_used",
]);
export const isWork = (event: Pick<ConnectionEvent, "kind">) => WORK_KINDS.has(event.kind);

/** How a row ended, when it ended other than asked. Success is silent. */
export type Badge = "waiting" | "stopped" | "failed" | "yes" | null;

/** The small mark a change wears instead of a face. */
export type ChangeNode = "plug" | "pause" | "people" | "list" | "rename" | "disconnect";

export type Chips = { from: string; to: string; toDecision: Decision | null };

/** One line of quiet text under a sentence; `danger` is the reason a row stopped or failed. */
export type Note = { text: string; tone: "muted" | "danger" };

export type Row = {
  /** The first event's ID: stable while the log refetches. */
  id: string;
  type: "work" | "change";
  who: Who;
  /** When it happened: the earliest event a folded row holds. */
  at: string;
  /** Newest first, everything the row stands for; never empty. */
  events: [ConnectionEvent, ...ConnectionEvent[]];
  /** Work rows lead with the name in medium weight; change rows read as one plain sentence. */
  subject: string | null;
  text: string;
  notes: Note[];
  chips: Chips | null;
  badge: Badge;
  node: ChangeNode | null;
  /** The same change repeated in the same minute, shown once as ×n. */
  repeats: number;
  /** A run of quiet reads folded into one row; its members open beneath it. */
  quiet: boolean;
  /** Work rows open in place to say what happened and why. */
  openable: boolean;
};

export type Day = {
  key: string;
  /** "Today", "Yesterday" or the weekday and date. */
  label: string;
  /** The date beside Today and Yesterday; empty for older days, whose label is the date. */
  date: string;
  rows: Row[];
  /** Who acted that day, most first: "Ada 10 · Iris 1 · You 3". */
  summary: string;
};

export type Group = "all" | "work" | "changes";

export const DECISION_WORDS: Record<Decision, string> = {
  always: "Always",
  ask_first: "Ask first",
  never: "Never",
};

const categoryLabel = (category: ActionCategory | null) =>
  category ? ACTION_CATEGORIES[category].label : null;

/**
 * A tool's own name in words: "GOOGLECALENDAR_DELETE_EVENT" for Google Calendar is "Delete event".
 * Short past-tense verbs per tool are not recorded yet, so this is what a sentence can name.
 */
export function actionName(tool: string | null, appSlug: string) {
  if (!tool) return "an action";
  const prefix = `${appSlug.replace(/[^a-z0-9]/gi, "").toUpperCase()}_`;
  const rest = tool.startsWith(prefix) ? tool.slice(prefix.length) : tool.replace(/^[^_]+_/, "");
  const words = rest.toLowerCase().replaceAll("_", " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : tool;
}

const DENIED_BECAUSE: Record<string, (category: string | null) => string> = {
  policy_never: (category) => (category ? `${category} is set to Never` : "It is set to Never"),
  no_grant: () => "It isn't allowed to use this account",
  approval_mismatch: () => "The approved request didn't match",
  approval_account_changed: () => "The account changed after it was approved",
  unknown_tool: () => "The action isn't known",
  not_connected: () => "The account wasn't connected",
  disconnected: () => "The account is disconnected",
  not_found: () => "The account wasn't found",
};

const str = (value: unknown) => (typeof value === "string" ? value : null);

const isDecision = (value: unknown): value is Decision =>
  value === "always" || value === "ask_first" || value === "never";

/** "12 seconds", "3 minutes": how long between two moments, for a sentence. */
export function duration(fromIso: string, toIso: string) {
  const seconds = Math.max(0, Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 1000));
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

export const clock = (iso: string, seconds = false) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
  });

/** What a permissions change says, from the before and after it recorded. */
function permissionsSentence(event: ConnectionEvent, appSlug: string) {
  const detail = event.detail ?? {};
  const target = str(detail["target"]);
  if (target === "lookOnly") {
    return {
      text: detail["to"] === true ? "turned on Look only" : "turned off Look only",
      chips: null,
    };
  }
  if (target === "category") {
    const key = str(detail["key"]) as ActionCategory | null;
    const label = key && key in ACTION_CATEGORIES ? ACTION_CATEGORIES[key].label : "a category";
    const from = isDecision(detail["from"]) ? detail["from"] : null;
    const to = isDecision(detail["to"]) ? detail["to"] : null;
    const text =
      to === "always"
        ? `let ${label} run without asking`
        : to === "ask_first"
          ? `set ${label} to ask first`
          : to === "never"
            ? `stopped ${label}`
            : `changed ${label}`;
    return {
      text,
      chips:
        from && to ? { from: DECISION_WORDS[from], to: DECISION_WORDS[to], toDecision: to } : null,
    };
  }
  if (target === "override") {
    const action = actionName(str(detail["key"]), appSlug);
    const from = isDecision(detail["from"]) ? detail["from"] : null;
    const to = isDecision(detail["to"]) ? detail["to"] : null;
    if (!to) return { text: `cleared the exception for ${action}`, chips: null };
    return {
      text: `set ${action} to ${DECISION_WORDS[to]}`,
      chips: {
        from: from ? DECISION_WORDS[from] : "As its category",
        to: DECISION_WORDS[to],
        toDecision: to,
      },
    };
  }
  return { text: "changed what teammates may do", chips: null };
}

/** One event as its row, before any folding. */
export function rowOf(
  event: ConnectionEvent,
  viewerId: string,
  app: { slug: string; name: string },
): Row {
  const who = whoOf(event, viewerId);
  const name = whoName(who);
  const base = {
    id: event.id,
    who,
    at: event.at,
    events: [event] satisfies Row["events"],
    notes: [] as Note[],
    chips: null as Chips | null,
    badge: null as Badge,
    node: null as ChangeNode | null,
    repeats: 1,
    quiet: false,
  };
  const category = categoryLabel(event.category);
  const action = actionName(event.tool, app.slug);
  const muted = (text: string): Note => ({ text, tone: "muted" });
  switch (event.kind) {
    case "tool_call": {
      const failed = event.outcome === "failure";
      const unknown = event.outcome === "unknown";
      return {
        ...base,
        type: "work",
        subject: name,
        text: failed
          ? `couldn't finish ${action}`
          : unknown
            ? `may not have finished ${action}`
            : `used ${action}`,
        notes: [
          ...(failed ? [{ text: `${app.name} said it didn't work`, tone: "danger" as const }] : []),
          ...(unknown ? [{ text: "We never heard how it ended", tone: "danger" as const }] : []),
          ...(category ? [muted(category)] : []),
        ],
        badge: failed || unknown ? "failed" : null,
        openable: true,
      };
    }
    case "tool_denied": {
      const because = DENIED_BECAUSE[event.outcome ?? ""]?.(category);
      return {
        ...base,
        type: "work",
        subject: name,
        text: `was stopped before ${action}`,
        notes: [
          { text: because ?? "It was stopped", tone: "danger" },
          muted(`nothing reached ${app.name}`),
        ],
        badge: "stopped",
        openable: true,
      };
    }
    case "approval_requested":
      return {
        ...base,
        type: "work",
        subject: name,
        text: `asked before ${action}`,
        notes: category ? [muted(`${category} asks first`)] : [],
        badge: "waiting",
        openable: true,
      };
    case "approval_decided": {
      const teammateKey = str(event.detail?.["teammate"]);
      const teammate =
        TEAMMATES.find((candidate) => candidate.key === teammateKey)?.name ?? "a teammate";
      const yes = event.outcome === "approve";
      const requestedAt = str(event.detail?.["requestedAt"]);
      return {
        ...base,
        type: "work",
        subject: name,
        text: yes ? `let ${teammate} use ${action}` : `said no to ${teammate} using ${action}`,
        notes: [
          muted(
            [
              requestedAt ? `${teammate} asked at ${clock(requestedAt)}` : null,
              requestedAt ? `answered ${duration(requestedAt, event.at)} later` : null,
              yes
                ? event.detail?.["remember"] === true
                  ? "and from now on"
                  : "just this once"
                : null,
            ]
              .filter(Boolean)
              .join(" · ") ||
              (category ?? ""),
          ),
        ].filter((note) => note.text),
        badge: yes ? "yes" : null,
        openable: true,
      };
    }
    case "connected":
    case "renewed": {
      const identity = str(event.detail?.["identity"]);
      return {
        ...base,
        type: "change",
        subject: null,
        text:
          event.kind === "connected"
            ? `${name} connected the account`
            : `${name} signed in again · access restored`,
        notes: identity ? [muted(identity)] : [],
        node: "plug",
        openable: false,
      };
    }
    case "paused":
    case "resumed":
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} ${event.kind} it`,
        node: "pause",
        openable: false,
      };
    case "disconnected":
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} disconnected it`,
        node: "disconnect",
        openable: false,
      };
    case "relabeled": {
      const from = str(event.detail?.["from"]);
      const to = str(event.detail?.["to"]);
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} renamed it`,
        chips: from && to ? { from, to, toDecision: null } : null,
        node: "rename",
        openable: false,
      };
    }
    case "access_changed":
      // Teammate grants were retired on Oct 17, 2026: old rows keep their words, past tense.
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} changed which teammates could use it`,
        node: "people",
        openable: false,
      };
    case "permissions_changed": {
      const { text, chips } = permissionsSentence(event, app.slug);
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} ${text}`,
        chips,
        node: "list",
        openable: false,
      };
    }
    // Chat access: plain sentences until the chat approvals land in the web.
    case "standing_approval_used":
      return {
        ...base,
        type: "work",
        subject: name,
        text: `used ${action} without asking, as chosen for a chat`,
        notes: category ? [muted(category)] : [],
        openable: true,
      };
    case "standing_approval_created":
    case "standing_approval_revoked":
      return {
        ...base,
        type: "change",
        subject: null,
        text:
          event.kind === "standing_approval_created"
            ? `${name} let ${action} run without asking in a chat`
            : `${name} made ${action} ask first again in a chat`,
        node: "list",
        openable: false,
      };
    case "availability_changed":
      return {
        ...base,
        type: "change",
        subject: null,
        text: `${name} changed whether chats can use it`,
        node: "people",
        openable: false,
      };
  }
}

/** A change's identity for repeats: same person, same kind, same detail. */
const sameChange = (a: ConnectionEvent, b: ConnectionEvent) =>
  a.kind === b.kind &&
  a.actorKind === b.actorKind &&
  a.actor === b.actor &&
  JSON.stringify(a.detail ?? null) === JSON.stringify(b.detail ?? null);

const minute = (iso: string) => Math.floor(Date.parse(iso) / 60_000);

/** A read that went fine, the only thing that folds into a run. */
const isQuietRead = (event: ConnectionEvent) =>
  event.kind === "tool_call" &&
  event.category === "look" &&
  (event.outcome === "success" || event.outcome === null) &&
  event.actorKind === "teammate";

const UNDO_WINDOW_MS = 5 * 60_000;

/**
 * One day's events, oldest first, as its rows, newest first. Three things fold, and nothing
 * else: a quiet run (back-to-back reads by one teammate), an undo pair (a pause resumed by the
 * same person within five minutes), and repeats (the same change by the same person in the same
 * minute). Stops, failures, sends, money, deletes and disconnects never fold.
 */
export function foldDay(
  oldestFirst: ConnectionEvent[],
  viewerId: string,
  app: { slug: string; name: string },
): Row[] {
  const rows: Row[] = [];
  for (const event of oldestFirst) {
    const last = rows.at(-1);
    const lastEvent = last?.events[0];

    if (
      last &&
      lastEvent &&
      isQuietRead(event) &&
      isQuietRead(lastEvent) &&
      lastEvent.actor === event.actor
    ) {
      last.events.unshift(event);
      const count = last.events.length;
      last.quiet = true;
      last.openable = false;
      last.text = `looked through ${app.name} ${count} times`;
      last.notes = [{ text: ACTION_CATEGORIES.look.label, tone: "muted" }];
      continue;
    }

    if (event.kind === "resumed") {
      const pair = rows.findLast(
        (row) =>
          row.events.length === 1 &&
          row.events[0].kind === "paused" &&
          row.events[0].actor === event.actor &&
          row.events[0].actorKind === event.actorKind &&
          Date.parse(event.at) - Date.parse(row.at) <= UNDO_WINDOW_MS,
      );
      if (pair) {
        pair.events.unshift(event);
        pair.text = `${whoName(pair.who)} paused it, then resumed it ${duration(pair.at, event.at)} later`;
        continue;
      }
    }

    if (
      last &&
      lastEvent &&
      last.type === "change" &&
      !isWork(event) &&
      event.kind !== "disconnected" &&
      sameChange(lastEvent, event) &&
      minute(lastEvent.at) === minute(event.at)
    ) {
      last.events.unshift(event);
      last.repeats += 1;
      continue;
    }

    rows.push(rowOf(event, viewerId, app));
  }
  return rows.reverse();
}

const dayKey = (iso: string) => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

function dayLabel(iso: string, now: Date) {
  const date = new Date(iso);
  const today = dayKey(now.toISOString());
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const short = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  if (dayKey(iso) === today) return { label: "Today", date: short };
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return { label: "Yesterday", date: short };
  return { label: short, date: "" };
}

/** "Ada 10 · Iris 1 · You 3": teammates first, most active first, then people, then Abacor. */
function summarize(events: ConnectionEvent[], viewerId: string) {
  const counts = new Map<WhoKey, { who: Who; count: number }>();
  for (const event of events) {
    const who = whoOf(event, viewerId);
    const entry = counts.get(whoKey(who)) ?? { who, count: 0 };
    entry.count += 1;
    counts.set(whoKey(who), entry);
  }
  const rank = (who: Who) => (who.kind === "teammate" ? 0 : who.kind === "abacor" ? 2 : 1);
  return [...counts.values()]
    .sort((a, b) => rank(a.who) - rank(b.who) || b.count - a.count)
    .map(({ who, count }) => `${whoName(who)} ${count}`)
    .join(" · ");
}

export const inGroup = (event: ConnectionEvent, group: Group) =>
  group === "all" || (group === "work" ? isWork(event) : !isWork(event));

/** The log as days, newest first, after the filters. Events arrive newest first. */
export function buildDays(
  newestFirst: ConnectionEvent[],
  {
    viewerId,
    app,
    group = "all",
    who = null,
    now = new Date(),
  }: {
    viewerId: string;
    app: { slug: string; name: string };
    group?: Group;
    who?: WhoKey | null;
    now?: Date;
  },
): Day[] {
  const shown = newestFirst.filter(
    (event) => inGroup(event, group) && (!who || whoKey(whoOf(event, viewerId)) === who),
  );
  const days = new Map<string, [ConnectionEvent, ...ConnectionEvent[]]>();
  for (const event of shown) {
    const key = dayKey(event.at);
    const day = days.get(key);
    days.set(key, day ? [...day, event] : [event]);
  }
  return [...days.entries()].map(([key, events]) => ({
    key,
    ...dayLabel(events[0].at, now),
    rows: foldDay([...events].reverse(), viewerId, app),
    summary: summarize(events, viewerId),
  }));
}

/** Everyone who appears in the log, for the Who filter, in the order the day summaries use. */
export function actorsOf(events: ConnectionEvent[], viewerId: string): Who[] {
  const seen = new Map<WhoKey, Who>();
  for (const event of events) {
    const who = whoOf(event, viewerId);
    if (!seen.has(whoKey(who))) seen.set(whoKey(who), who);
  }
  const rank = (who: Who) => (who.kind === "teammate" ? 0 : who.kind === "abacor" ? 2 : 1);
  return [...seen.values()].sort((a, b) => rank(a) - rank(b));
}
